/**
 * Types for the MobX-bound input components (`TextBox`, `Combobox`, `DateTimePicker`, …).
 *
 * These components take a `model` plus a `modelProperty` name and write the user's input
 * straight onto `model[modelProperty]`. Historically `modelProperty` was typed `string`,
 * which meant the write was an implicit-any index access — only tolerated because
 * `suppressImplicitAnyIndexErrors` was set in `tsconfig.json`. That flag was removed from
 * TypeScript in 5.5, so the binding is now expressed in the type system instead.
 *
 * NOTE: consider these components legacy for *new* work — forms being written or reworked
 * now use React Hook Form + Zod (see `src/Modules/UserAccount`). This layer is still in
 * heavy use though, so it is typed rather than left loose.
 */

/**
 * The keys of `T` whose value type is compatible with `V`.
 *
 * `NonNullable<T[K]>` is used so that an optional field (`note?: string`) is just as
 * bindable as a required one (`note: string`) — writing `undefined` back is exactly how
 * these inputs clear a value.
 */
type KeysOfValue<T, V> = { [K in keyof T]-?: NonNullable<T[K]> extends V ? K : never }[keyof T];

/**
 * The `modelProperty` of a bound input: a key of `T` that actually holds a `V`.
 *
 * This catches both typo'd property names and binding an input to a field of the wrong
 * type (e.g. pointing a `TextBox` at a `number` field).
 *
 * The intersection with `string` keeps the prop assignable from a string literal and
 * excludes the `number`/`symbol` half of `keyof`.
 */
export type BoundProp<T, V> = KeysOfValue<T, V> & string;

/**
 * Write a value onto a bound model property.
 *
 * The cast is unavoidable: TypeScript rejects writes through a generic indexed access
 * (`model[key] = value`) because `T[K]` could be instantiated with a narrower subtype than
 * the value being written — see the `TS2322 "could be instantiated with a different
 * subtype"` error. `BoundProp` has already constrained `K` to a key whose value type
 * accepts `V`, so the write is sound; the compiler just cannot prove it. Confining the cast
 * to this one helper keeps it out of the individual components.
 */
export function setBoundValue<T, V>(model: T, property: BoundProp<T, V>, value: V | undefined): void {
	(model as Record<string, unknown>)[property] = value;
}

/**
 * Read a bound model property back out as `V`.
 *
 * Counterpart to {@link setBoundValue}, for the components that need the current value to
 * render (checked state, input value, the selected combobox option, …). Same reasoning as
 * above: `BoundProp` guarantees the property holds a `V`, but a generic indexed access
 * resolves to a union the compiler will not narrow on its own.
 */
export function getBoundValue<T, V>(model: T, property: BoundProp<T, V>): V | undefined {
	return (model as Record<string, unknown>)[property] as V | undefined;
}

/**
 * A scratch model for an input whose value is *not* driven by the model binding.
 *
 * A few call sites drive one of these inputs entirely through `flatpickrProps` or
 * `inputProps` — supplying the value and the change handler directly — but still have to pass
 * `model`/`modelProperty` because they are required props. Those sites used to pass `{}` with
 * a placeholder name like `"range"` or `""`, which only compiled because implicit-any index
 * errors were suppressed.
 *
 * `model={unboundModel<Date>()} modelProperty="unbound"` says the same thing honestly: there
 * is a real property of the right type for the input to write to, and nothing reads it.
 */
export interface UnboundModel<V> {
	unbound?: V;
}

export function unboundModel<V>(): UnboundModel<V> {
	return {};
}
